import React, { useEffect, useMemo, useRef, useState } from "react"
import _ from "lodash"
import { useSelector, useDispatch } from "react-redux"
import { Message, toaster } from "rsuite"
import {
    Alert,
    Button,
    IconButton,
    LinearProgress,
    MenuItem,
    TextField,
    Tooltip
} from "@mui/material"
import {
    Add,
    ContentCopy,
    ContentPaste,
    DeleteOutline,
    EditOutlined,
    EventNoteOutlined,
    GroupsOutlined,
    ImageOutlined,
    ImportExportOutlined,
    LinkOutlined,
    LockOutlined,
    PlaylistAddCheck,
    RefreshOutlined,
    RemoveRedEyeOutlined,
    SaveOutlined,
    SchoolOutlined,
    TuneOutlined
} from "@mui/icons-material"

import { GetCurrentUser, GetUserPlatformId, GetUserPrivillages, ExamGradeRemarks, Gradding } from "../utils/hooks"
import { Loader } from "../components/index"
import ClassList from "../components/ClassList"
import { useSnackbar } from "../components/Snakebar"
import { downloadLinksPdf } from "../components/LinksPdf"
import EmptyState from "../Lectures/components/EmptyState"
import { initials, saveBlob, sortByRollNo } from "../StockManager/classroom/helpers"
import ImportDataModal from "./component/ImportModal"
import ImportResultPopover from "./component/ImportDataTable"
import ResultViewDialog from "./component/ResultPopoverTable"
import FullScreenImageViewer from "./component/TemplateView"
import ApplyConfigDialog from "./component/ApplyConfigDialog"

import { updateFields, resetForm } from "../Redux/actions/formValue.action"
import { useLazyGetInstitutionClassesQuery } from "../Redux/actions/classSetup.action"
import {
    useLazyGetAllExamsQuery,
    useLazyDownloadResultTemplateQuery,
    useImportResultMutation,
    usePublishResultsMutation,
    useLazyViewExamResultDetailsQuery,
    usePublishMinorSubjectResultsMutation,
    useLazyGetExamMarkConfigurationQuery,
    useConfigureExamMarkCriteriaMutation,
    useEditExamMarkCriteriaMutation,
    useLazyDownloadSubjectWiseResultImportTemplateQuery,
    useUpdateStudentMarksMutation,
    useLazyDownloadMinorSubjectResultTemplateQuery
} from "../Redux/actions/examAction"
import { useLazyGetClassStudentsQuery } from "../Redux/actions/studentAction"
import { useLazyMarksheetTemplateActionQuery } from "../Redux/actions/certificate.action"
import { useLazyGetInstitutionMetadataQuery, useUpdateInstitutionMetadataMutation } from "../Redux/actions/setting.action"

const pillButtonSx = { textTransform: "none", borderRadius: "10px", whiteSpace: "nowrap" }
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff" } }

// Keys ResultViewDialog keeps in the shared form state while editing marks
const RESULT_VIEW_KEYS = { editStatus: null, theory: null, practical: null, editMark: null }

/*
 * The dialogs below were written against table rows of this shape, so the
 * selected exam is still handed to them like this.
 */
const toExamRow = (exam, index = 0) => ({
    SL_NO: index + 1,
    Examination_Name: exam.exam_name,
    Exam_Id: exam.id
})

/** What the signed-in user may do here. Admins may do everything. */
const resolvePermissions = () => {
    const privileges = GetUserPrivillages()
    if (privileges === "admin") return { delete: true, update: true, create: true }
    if (!Array.isArray(privileges) || privileges.length === 0) return null

    const allowed = {}
    if (privileges.includes("Delete Content")) allowed.delete = true
    if (privileges.includes("Create Content")) allowed.create = true
    if (privileges.includes("Upload Content")) allowed.update = true
    return Object.keys(allowed).length > 0 ? allowed : null
}

const errorDetail = (error, fallback) => _.get(error, "data.detail", "") || fallback

export default function ResultRoot() {
    const dispatch = useDispatch()
    const { showSnackbar } = useSnackbar()
    const formData = useSelector((state) => state.form.state)
    const permissions = useMemo(resolvePermissions, [])
    const canCreate = Boolean(permissions?.create)

    const [activeClassId, setActiveClassId] = useState(null)
    const [tab, setTab] = useState("exams")
    const [selectedRow, setSelectedRow] = useState(null)
    const [openImport, setOpenImport] = useState(false)
    const [tempCTableData, setTempCTable] = useState([])
    const [openTempCTable, setOpenTempCTable] = useState(false)
    const [openPreviewResult, setOpenPreviewResult] = useState(false)
    const [disableAllFields, setDisableAllFields] = useState(false)
    // The exam's configuration as last saved; null when it has none yet.
    // Cancel restores it, and Save updates it instead of creating a new one.
    const [savedConfig, setSavedConfig] = useState(null)
    const [isSubjectWiseTemplate, setIsSubWiseTemplate] = useState(false)
    const [isMinorSubjectTemplate, setIsMinorSubjectTemplate] = useState(false)
    const [subjectName, setSubjectName] = useState("")
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [openTemplateView, setOpenTemplateView] = useState(false)
    const [copiedConfig, setCopiedConfig] = useState(null)
    const [openApplyConfig, setOpenApplyConfig] = useState(false)
    const [applyingConfig, setApplyingConfig] = useState(false)
    const [linksExamId, setLinksExamId] = useState(null)
    const latestConfigExam = useRef(null)

    const [getClasses, classesState] = useLazyGetInstitutionClassesQuery()
    const [getAllExamination, examsState] = useLazyGetAllExamsQuery()
    const [downloadTemplate, downloadTemplateState] = useLazyDownloadResultTemplateQuery()
    const [importResult, importResultState] = useImportResultMutation()
    const [publishResults, publishResultState] = usePublishResultsMutation()
    const [minorSubjectResultPublishApi, minorPublishState] = usePublishMinorSubjectResultsMutation()
    const [getClassResultReview, resultReviewState] = useLazyViewExamResultDetailsQuery()
    const [createMarkConfigApi, createMarkConfigState] = useConfigureExamMarkCriteriaMutation()
    const [bulkMarkConfigApi] = useConfigureExamMarkCriteriaMutation()
    const [editMarkConfigApi, editMarkConfigState] = useEditExamMarkCriteriaMutation()
    const [getMarkConfigApi, markConfigState] = useLazyGetExamMarkConfigurationQuery()
    const [getStudentsApi] = useLazyGetClassStudentsQuery()
    // The open class's students, shown in the Students tab
    const [getClassStudents, classStudentsState] = useLazyGetClassStudentsQuery()
    const [downloadSubWiseTemplateApi, subWiseTemplateState] = useLazyDownloadSubjectWiseResultImportTemplateQuery()
    const [downloadMinorSubjectTemplate, minorTemplateState] = useLazyDownloadMinorSubjectResultTemplateQuery()
    const [updateStudentMarkApi, updateStudentMarkState] = useUpdateStudentMarksMutation()
    const [getMarksheetTemplates, templatesState] = useLazyMarksheetTemplateActionQuery()
    const [getSchoolMetadata, schoolMetadataState] = useLazyGetInstitutionMetadataQuery()
    const [updateSchoolMetadata, updateSchoolMetadataState] = useUpdateInstitutionMetadataMutation()

    /* ------------------------------ data ------------------------------ */

    const classes = useMemo(() => {
        const list = _.get(classesState, "data.list", [])
        return Array.isArray(list) ? list : []
    }, [classesState])

    const examList = useMemo(() => {
        const list = _.get(examsState, "data.list", [])
        return Array.isArray(list) ? list : []
    }, [examsState])

    const activeClass = useMemo(
        () => classes.find((item) => item.id === activeClassId) || null,
        [classes, activeClassId]
    )

    const examsOf = (item) =>
        examList.filter((exam) => exam.school_ref === item.institution_ref && exam.class_ref === item.id)

    const classExamList = useMemo(
        () => (activeClass ? examsOf(activeClass) : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [examList, activeClass]
    )

    const templates = useMemo(() => {
        const list = _.get(templatesState, "data.data", [])
        return Array.isArray(list) ? list : []
    }, [templatesState])

    const schoolMetadata = useMemo(
        () => (schoolMetadataState.isSuccess ? _.get(schoolMetadataState, "currentData.metadata.meta_data", null) : null),
        [schoolMetadataState]
    )
    const savedTemplate = _.get(schoolMetadata, "certificate_template", null)

    // Show the school's saved marksheet template until the user picks another
    useEffect(() => {
        if (savedTemplate) setSelectedTemplate(savedTemplate)
    }, [savedTemplate])

    const templateImage = useMemo(() => {
        if (!selectedTemplate) return ""
        const match = templates.find(
            (item) => item.id === selectedTemplate.id || item.name === selectedTemplate.name
        )
        return match?.image || ""
    }, [templates, selectedTemplate])

    const resultData = useMemo(
        () => (resultReviewState.isSuccess ? _.get(resultReviewState, "currentData.data.results", []) : []),
        [resultReviewState]
    )
    // Published separately from the main marks; each entry is { roll_no, marks: [{ subject_name, grade }] }
    const minorResultData = useMemo(
        () => (resultReviewState.isSuccess ? _.get(resultReviewState, "currentData.minor_results", []) || [] : []),
        [resultReviewState]
    )

    const classStudents = useMemo(() => {
        if (!activeClass) return []
        const list = _.get(classStudentsState, "currentData.list", [])
        return Array.isArray(list) ? sortByRollNo(list) : []
    }, [activeClass, classStudentsState])

    const dynamicForm = _.get(formData, "dynamicForm", [])
    const configExamId = _.get(formData, "exam_id", "")

    /* ----------------------------- fetching ---------------------------- */

    const fetchPreData = () => {
        const user_platform = _.get(GetCurrentUser(), "meta_data.user_platform", null)
        if (!user_platform) return
        getClasses({ institution_ref: user_platform })
        getAllExamination({ school_id: user_platform })
        getMarksheetTemplates()
        getSchoolMetadata(user_platform)
    }

    const fetchClassStudents = (item = activeClass) => {
        if (!item?.id || !item?.institution_ref) return
        getClassStudents({ school_id: item.institution_ref, class_id: item.id })
    }

    useEffect(() => {
        fetchPreData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const refresh = () => {
        fetchPreData()
        fetchClassStudents()
    }

    const selectClass = (item) => {
        setActiveClassId(item.id)
        fetchClassStudents(item)
        setSelectedRow(null)
        dispatch(resetForm())
        setDisableAllFields(false)
        setSavedConfig(null)
        setSubjectName("")
        setIsSubWiseTemplate(false)
        setIsMinorSubjectTemplate(false)
        latestConfigExam.current = null
    }

    // Open the first class once classes arrive, or if the open one disappears
    useEffect(() => {
        if (classes.length > 0 && !activeClass) selectClass(classes[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes])

    const fetchResultPreview = (exam) => {
        if (!exam?.school_ref || !exam?.class_ref || !exam?.id) return
        getClassResultReview({ school_id: exam.school_ref, class_id: exam.class_ref, exam_id: exam.id })
    }

    const examForRow = (row = selectedRow) => classExamList.find((exam) => exam.id === row?.Exam_Id) || null

    /* ---------------------------- marksheet ----------------------------- */

    const applyTemplate = async () => {
        const schoolId = GetUserPlatformId()
        if (!schoolId || !schoolMetadata || !selectedTemplate) return
        try {
            const result = await updateSchoolMetadata({
                platform_id: schoolId,
                meta_data: { ...schoolMetadata, certificate_template: { ...selectedTemplate } }
            }).unwrap()
            showSnackbar({ message: _.get(result, "message", "Marksheet template updated."), severity: "success" })
            getSchoolMetadata(schoolId)
        } catch {
            showSnackbar({ message: "Error while update metadata,Please try after sometime.", severity: "error" })
        }
    }

    /* --------------------------- exam actions --------------------------- */

    const openPublish = (exam, index) => {
        setSelectedRow(toExamRow(exam, index))
        setOpenImport(true)
    }

    const openResults = (exam, index) => {
        setSelectedRow(toExamRow(exam, index))
        fetchResultPreview(exam)
        setOpenPreviewResult(true)
    }

    const downloadResultLinks = async (exam) => {
        setLinksExamId(exam.id)
        try {
            const students = await getStudentsApi({ school_id: exam.school_ref, class_id: exam.class_ref }).unwrap()
            const list = sortByRollNo(_.get(students, "list", []))
            if (list.length === 0) {
                showSnackbar({ message: "There are no students in this class yet.", severity: "info" })
                return
            }
            const className = activeClass ? activeClass.class_name : ""
            await downloadLinksPdf({
                title: "Student Result Download Links",
                linkHeader: "Result Download Link",
                rows: list.map((item) => ({
                    rollNo: item.roll_no,
                    name: item.name,
                    link: `${window.location.origin}/results/${item.school_ref}/${item.class_ref}/${exam.id}/${item.id}`
                })),
                className,
                examName: exam.exam_name,
                fileName: `${className ? className + "_" : ""}${exam.exam_name}_result_links.pdf`.replace(/\s+/g, "_")
            })
        } catch (error) {
            showSnackbar({
                message: errorDetail(error, "Error while generating PDF, Please try again"),
                severity: "error"
            })
        } finally {
            setLinksExamId(null)
        }
    }

    /* -------------------- import / publish (dialogs) -------------------- */

    const handleImportResult = async (file) => {
        try {
            const data = await importResult(file).unwrap()
            setTempCTable(data)
            setOpenImport(false)
            setOpenTempCTable(true)
        } catch (error) {
            setTempCTable([])
            showSnackbar({ message: errorDetail(error, "Couldn't read that file. Please check the template and try again."), severity: "error" })
        }
    }

    const handlePublish = async (dataInfo) => {
        const publish = isMinorSubjectTemplate ? minorSubjectResultPublishApi : publishResults
        try {
            const result = await publish(dataInfo).unwrap()
            const count = _.get(result, "inserted_count", "")
            toaster.push(
                <Message type="success">Result Publish successfull for the count of {count} students</Message>,
                { placement: "topCenter" }
            )
            setOpenTempCTable(false)
        } catch {
            toaster.push(
                <Message type="error">Error while Publish results,Please try again after sometime</Message>,
                { placement: "topCenter" }
            )
        }
    }

    const downloadWithFeedback = async (request, filename, wrapAsXlsx = false) => {
        try {
            const data = await request.unwrap()
            const blob = wrapAsXlsx
                ? new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
                : data
            saveBlob(blob, filename)
        } catch (error) {
            showSnackbar({ message: errorDetail(error, "Couldn't download the template. Please try again."), severity: "error" })
        }
    }

    const handleDownloadTemplate = () => {
        const exam = examForRow()
        if (!exam) return
        downloadWithFeedback(
            downloadTemplate({ school_ref: exam.school_ref, class_ref: exam.class_ref, exam_ref: exam.id }),
            "result_template.xlsx",
            true
        )
    }

    const handleDownloadSubjectTemplate = () => {
        const exam = examForRow()
        if (!exam || !subjectName) return
        downloadWithFeedback(
            downloadSubWiseTemplateApi({
                school_ref: exam.school_ref,
                class_ref: exam.class_ref,
                exam_ref: exam.id,
                subject_name: subjectName
            }),
            "importSubjectWiseResultTemplate.xlsx"
        )
    }

    const handleDownloadMinorTemplate = () => {
        const exam = examForRow()
        if (!exam) return
        downloadWithFeedback(
            downloadMinorSubjectTemplate({ class_id: exam.class_ref, school_id: exam.school_ref }),
            "minorSubjects.xlsx"
        )
    }

    const updateStudentMark = async (data) => {
        const exam = examForRow()
        if (!data || !exam) return
        try {
            const result = await updateStudentMarkApi({
                school_id: exam.school_ref,
                class_ref: exam.class_ref,
                exam_ref: exam.id,
                results: [{ ...data }]
            }).unwrap()
            showSnackbar({ message: _.get(result, "message", "Marks updated."), severity: "success" })
            fetchResultPreview(exam)
        } catch (error) {
            showSnackbar({ message: errorDetail(error, "Couldn't update the marks. Please try again."), severity: "error" })
        }
    }

    /* -------------------------- mark config ----------------------------- */

    const selectConfigExam = async (examId) => {
        const exam = classExamList.find((item) => item.id === examId)
        if (!exam) return

        dispatch(updateFields({ exam_id: exam.id, school_ref: exam.school_ref, class_ref: exam.class_ref }))
        latestConfigExam.current = exam.id

        try {
            const result = await getMarkConfigApi({ exam_id: exam.id }).unwrap()
            if (latestConfigExam.current !== exam.id) return
            const config = _.get(result, "data", {})
            const saved = {
                total_mark: _.get(config, "total_mark", ""),
                pass_marks: _.get(config, "pass_mark", ""),
                dynamicForm: Object.values(_.get(config, "gradding", {}) || {})
            }
            dispatch(updateFields({
                school_ref: _.get(config, "school_ref", ""),
                exam_id: _.get(config, "exam_ref", ""),
                class_ref: _.get(config, "class_ref", ""),
                ...saved
            }))
            setSavedConfig(saved)
            setDisableAllFields(true)
        } catch {
            if (latestConfigExam.current !== exam.id) return
            // No configuration yet: start this exam from a blank form
            dispatch(updateFields({ total_mark: "", pass_marks: "", dynamicForm: [] }))
            setSavedConfig(null)
            setDisableAllFields(false)
        }
    }

    const startEditConfig = () => {
        if (!canCreate) {
            showSnackbar({ message: "Not Allow to edit!,Please connect with Admin", severity: "info" })
            return
        }
        setDisableAllFields(false)
    }

    const cancelEditConfig = () => {
        if (!savedConfig) return
        dispatch(updateFields({
            total_mark: savedConfig.total_mark,
            pass_marks: savedConfig.pass_marks,
            dynamicForm: savedConfig.dynamicForm.map((field) => ({ ...field }))
        }))
        setDisableAllFields(true)
    }

    const updateGradeRow = (index, patch) => {
        const update = dynamicForm.map((item, i) => (i === index ? { ...item, ...patch } : item))
        dispatch(updateFields({ dynamicForm: update }))
    }

    const addGradeRow = () => {
        const used = dynamicForm.map((row) => row.label)
        const nextGrade = Gradding.find((grade) => !used.includes(grade)) || ""
        dispatch(updateFields({ dynamicForm: [...dynamicForm, { label: nextGrade, value: "" }] }))
    }

    const removeGradeRow = (index) => {
        dispatch(updateFields({ dynamicForm: dynamicForm.filter((_row, i) => i !== index) }))
    }

    const handleSubmit = async () => {
        if (!canCreate) {
            showSnackbar({ message: "Not Allow to create!,Please connect with Admin", severity: "info" })
            return
        }
        const exam_id = _.get(formData, "exam_id", null)
        const pass_marks = _.get(formData, "pass_marks", null)
        const total_mark = _.get(formData, "total_mark", null)
        const class_ref = _.get(formData, "class_ref", null)
        const school_ref = _.get(formData, "school_ref", null)
        const incompleteRow = dynamicForm.some((row) => !row.label || row.value === "" || row.value == null)

        if (!exam_id || !pass_marks || !total_mark || dynamicForm.length === 0 || !school_ref || !class_ref || incompleteRow) {
            showSnackbar({
                message: "Fill all fields data and add fields for calculate grade or devision",
                severity: "info"
            })
            return
        }

        if (Number(pass_marks) > Number(total_mark)) {
            showSnackbar({ message: "Pass marks cannot be more than the total marks", severity: "info" })
            return
        }

        const payload = {
            school_ref,
            class_ref,
            exam_ref: exam_id,
            total_mark,
            pass_mark: pass_marks,
            gradding: { ...dynamicForm }
        }
        try {
            const result = savedConfig
                ? await editMarkConfigApi(payload).unwrap()
                : await createMarkConfigApi(payload).unwrap()
            showSnackbar({
                message: _.get(result, "message", savedConfig ? "Configuration updated." : "Configuration saved."),
                severity: "success"
            })
            // Saved: lock the form again so it reads as the current configuration
            setSavedConfig({ total_mark, pass_marks, dynamicForm: dynamicForm.map((field) => ({ ...field })) })
            setDisableAllFields(true)
        } catch (error) {
            showSnackbar({ message: errorDetail(error, "Couldn't save the configuration."), severity: "error" })
        }
    }

    const handleCopyConfig = () => {
        const total_mark = _.get(formData, "total_mark", "")
        const pass_marks = _.get(formData, "pass_marks", "")
        if (!total_mark || !pass_marks || dynamicForm.length === 0) {
            showSnackbar({
                message: "Nothing to copy, fill total marks, pass marks and grade fields first",
                severity: "info"
            })
            return
        }
        const sourceExam = examList.find((exam) => exam.id === configExamId)
        setCopiedConfig({
            total_mark,
            pass_marks,
            dynamicForm: dynamicForm.map((field) => ({ ...field })),
            sourceExamId: sourceExam ? sourceExam.id : null,
            sourceLabel: `${activeClass ? activeClass.class_name : ""}${sourceExam ? ` - ${sourceExam.exam_name}` : ""}`
        })
        showSnackbar({ message: "Configuration copied, paste it in any class or exam", severity: "success" })
    }

    const handlePasteConfig = () => {
        if (!copiedConfig) return
        dispatch(updateFields({
            total_mark: copiedConfig.total_mark,
            pass_marks: copiedConfig.pass_marks,
            dynamicForm: copiedConfig.dynamicForm.map((field) => ({ ...field }))
        }))
    }

    const handleApplyConfig = async (targetExams) => {
        if (!canCreate) {
            showSnackbar({ message: "Not Allow to create!,Please connect with Admin", severity: "info" })
            return
        }
        if (!copiedConfig || targetExams.length === 0) return

        setApplyingConfig(true)
        const results = await Promise.allSettled(targetExams.map((exam) => bulkMarkConfigApi({
            school_ref: exam.school_ref,
            class_ref: exam.class_ref,
            exam_ref: exam.id,
            total_mark: copiedConfig.total_mark,
            pass_mark: copiedConfig.pass_marks,
            gradding: { ...copiedConfig.dynamicForm }
        }).unwrap()))
        setApplyingConfig(false)

        const failed = targetExams.filter((_exam, i) => results[i].status === "rejected")
        if (failed.length === 0) {
            showSnackbar({ message: `Configuration applied to ${targetExams.length} exam(s)`, severity: "success" })
            setOpenApplyConfig(false)
        } else {
            const reason = _.get(results.find((r) => r.status === "rejected"), "reason.data.detail", "")
            showSnackbar({
                message: `Applied to ${targetExams.length - failed.length} exam(s), failed for: ${failed.map((exam) => exam.exam_name).join(", ")}${reason ? ` (${reason})` : ""}`,
                severity: "error"
            })
        }
    }

    /* ------------------------------- view ------------------------------- */

    const loadingClasses = classesState.isLoading || classesState.isFetching
    const blocking =
        classesState.isLoading ||
        examsState.isLoading ||
        downloadTemplateState.isFetching ||
        importResultState.isLoading ||
        publishResultState.isLoading ||
        minorPublishState.isLoading ||
        resultReviewState.isFetching ||
        createMarkConfigState.isLoading ||
        editMarkConfigState.isLoading ||
        subWiseTemplateState.isFetching ||
        minorTemplateState.isFetching ||
        updateStudentMarkState.isLoading ||
        updateSchoolMetadataState.isLoading

    const examCountFor = (item) => {
        const count = examsOf(item).length
        return `${count} exam${count === 1 ? "" : "s"}`
    }

    const templateChanged = selectedTemplate && selectedTemplate.id !== savedTemplate?.id

    const renderExams = () => {
        if (classExamList.length === 0) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<EventNoteOutlined />}
                        title="No examinations yet"
                        description="Create an examination for this class to publish its results."
                    />
                </div>
            )
        }

        return (
            <ul className="divide-y divide-slate-100">
                {classExamList.map((exam, index) => (
                    <li
                        key={exam.id}
                        className="flex flex-col gap-3 px-4 py-4 sm:px-5 md:flex-row md:items-center md:justify-between"
                    >
                        <div className="flex min-w-0 items-center gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                                <EventNoteOutlined fontSize="small" />
                            </span>
                            <div className="min-w-0">
                                <div className="truncate font-medium text-slate-800">{exam.exam_name}</div>
                                <div className="truncate text-xs text-slate-500">
                                    Exam #{exam.id}
                                    {exam.created_at ? ` · Created ${exam.created_at}` : ""}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                size="small"
                                variant="contained"
                                disableElevation
                                startIcon={<ImportExportOutlined fontSize="small" />}
                                onClick={() => openPublish(exam, index)}
                                sx={{ ...pillButtonSx, borderRadius: "8px" }}
                            >
                                Publish result
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<RemoveRedEyeOutlined fontSize="small" />}
                                onClick={() => openResults(exam, index)}
                                sx={{ ...pillButtonSx, borderRadius: "8px" }}
                            >
                                View result
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                startIcon={<LinkOutlined fontSize="small" />}
                                onClick={() => downloadResultLinks(exam)}
                                disabled={linksExamId !== null}
                                sx={{ ...pillButtonSx, borderRadius: "8px", borderColor: "#e2e8f0", color: "#334155" }}
                            >
                                {linksExamId === exam.id ? "Preparing…" : "Result links"}
                            </Button>
                        </div>
                    </li>
                ))}
            </ul>
        )
    }

    const renderStudents = () => {
        if (classStudentsState.isError && !classStudentsState.isFetching) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<GroupsOutlined />}
                        title="Couldn't load students"
                        description="Please check your connection and try again."
                        actionLabel="Retry"
                        onAction={() => fetchClassStudents()}
                    />
                </div>
            )
        }

        if (classStudentsState.isFetching && classStudents.length === 0) {
            return (
                <div className="space-y-3 p-5">
                    {[0, 1, 2, 3, 4].map((key) => (
                        <div key={key} className="h-12 animate-pulse rounded-lg bg-slate-100" />
                    ))}
                </div>
            )
        }

        if (classStudents.length === 0) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<GroupsOutlined />}
                        title="No students yet"
                        description="No students are currently enrolled in this class."
                    />
                </div>
            )
        }

        return (
            <ul className="divide-y divide-slate-100">
                {classStudents.map((student) => (
                    <li key={student.id ?? student.roll_no} className="flex items-center gap-3 px-4 py-3 sm:px-5">
                        <span className="inline-flex min-w-[2.5rem] justify-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-700">
                            {student.roll_no ?? "-"}
                        </span>
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                            {initials(student.name)}
                        </span>
                        <div className="min-w-0 flex-1">
                            <div className="truncate font-medium text-slate-800">{student.name || "-"}</div>
                            <div className="truncate text-xs text-slate-500">
                                {[student.email, student.dob && `DOB ${student.dob}`].filter(Boolean).join(" · ") || "-"}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        )
    }

    const renderConfig = () => {
        if (classExamList.length === 0) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<TuneOutlined />}
                        title="No examinations to configure"
                        description="Create an examination for this class first."
                    />
                </div>
            )
        }

        return (
            <div className="space-y-5 p-4 sm:p-5">
                <div className="grid gap-4 sm:grid-cols-3">
                    <TextField
                        select
                        size="small"
                        label="Examination"
                        value={classExamList.some((exam) => exam.id === configExamId) ? configExamId : ""}
                        onChange={(e) => selectConfigExam(e.target.value)}
                        sx={fieldSx}
                    >
                        {classExamList.map((exam) => (
                            <MenuItem key={exam.id} value={exam.id}>
                                {exam.exam_name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        size="small"
                        type="number"
                        label="Total marks"
                        value={_.get(formData, "total_mark", "")}
                        onChange={(e) => dispatch(updateFields({ total_mark: e.target.value }))}
                        disabled={disableAllFields || !configExamId}
                        sx={fieldSx}
                    />
                    <TextField
                        size="small"
                        type="number"
                        label="Pass marks"
                        value={_.get(formData, "pass_marks", "")}
                        onChange={(e) => dispatch(updateFields({ pass_marks: e.target.value }))}
                        disabled={disableAllFields || !configExamId}
                        sx={fieldSx}
                    />
                </div>

                {disableAllFields && (
                    <Alert
                        icon={<LockOutlined fontSize="small" />}
                        severity="info"
                        sx={{ borderRadius: "10px", alignItems: "center" }}
                        action={
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditOutlined fontSize="small" />}
                                onClick={startEditConfig}
                                sx={{ textTransform: "none", borderRadius: "8px", whiteSpace: "nowrap", bgcolor: "white" }}
                            >
                                Edit configuration
                            </Button>
                        }
                    >
                        This exam already has a saved configuration. Edit it to change the marks
                        or grades, or copy it to other exams.
                    </Alert>
                )}

                {savedConfig && !disableAllFields && (
                    <Alert severity="warning" sx={{ borderRadius: "10px" }}>
                        You are editing a saved configuration. The new marks and grades also apply
                        to results already published for this exam.
                    </Alert>
                )}

                {configExamId ? (
                    <div className="rounded-xl border border-slate-200">
                        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                            <div>
                                <h3 className="text-sm font-semibold text-slate-800">Grades &amp; divisions</h3>
                                <p className="text-xs text-slate-500">The minimum value needed for each grade, and its remark.</p>
                            </div>
                            <Button
                                size="small"
                                startIcon={<Add />}
                                onClick={addGradeRow}
                                disabled={disableAllFields}
                                sx={{ textTransform: "none", borderRadius: "8px", whiteSpace: "nowrap" }}
                            >
                                Add grade
                            </Button>
                        </div>

                        {dynamicForm.length === 0 ? (
                            <p className="px-4 py-6 text-center text-sm text-slate-500">
                                No grades yet. Add one to start.
                            </p>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {dynamicForm.map((field, index) => (
                                    <div
                                        key={index}
                                        className="grid grid-cols-2 items-center gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)_auto]"
                                    >
                                        <TextField
                                            select
                                            size="small"
                                            label="Grade"
                                            value={Gradding.includes(field.label) ? field.label : ""}
                                            onChange={(e) => updateGradeRow(index, { label: e.target.value })}
                                            disabled={disableAllFields}
                                            sx={fieldSx}
                                            SelectProps={{ renderValue: () => field.label || "" }}
                                        >
                                            {Gradding.map((grade) => (
                                                <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                                            ))}
                                        </TextField>
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Min value"
                                            value={_.get(field, "value", "")}
                                            onChange={(e) => updateGradeRow(index, { value: e.target.value })}
                                            disabled={disableAllFields}
                                            sx={fieldSx}
                                        />
                                        <TextField
                                            select
                                            size="small"
                                            label="Remark"
                                            value={_.get(field, "remark", "")}
                                            onChange={(e) => updateGradeRow(index, { remark: e.target.value })}
                                            disabled={disableAllFields}
                                            sx={fieldSx}
                                        >
                                            {ExamGradeRemarks.map((remark) => (
                                                <MenuItem key={remark} value={remark}>{remark}</MenuItem>
                                            ))}
                                        </TextField>
                                        <div className="flex justify-end">
                                            <Tooltip title="Remove grade" arrow>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        aria-label="Remove grade"
                                                        onClick={() => removeGradeRow(index)}
                                                        disabled={disableAllFields}
                                                    >
                                                        <DeleteOutline fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
                        Pick an examination to set its total marks, pass marks and grades.
                    </p>
                )}

                {copiedConfig && (
                    <p className="text-xs text-slate-500">
                        Copied: <span className="font-medium text-slate-700">{copiedConfig.sourceLabel || "configuration"}</span>
                    </p>
                )}

                <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-wrap gap-2">
                        <Button size="small" variant="outlined" startIcon={<ContentCopy fontSize="small" />} onClick={handleCopyConfig} sx={{ ...pillButtonSx, borderRadius: "8px" }}>
                            Copy
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ContentPaste fontSize="small" />}
                            onClick={handlePasteConfig}
                            disabled={!copiedConfig || disableAllFields}
                            sx={{ ...pillButtonSx, borderRadius: "8px" }}
                        >
                            Paste
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PlaylistAddCheck fontSize="small" />}
                            onClick={() => setOpenApplyConfig(true)}
                            disabled={!copiedConfig}
                            sx={{ ...pillButtonSx, borderRadius: "8px" }}
                        >
                            Apply to classes / exams
                        </Button>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                        {savedConfig && !disableAllFields && (
                            <Button
                                onClick={cancelEditConfig}
                                disabled={editMarkConfigState.isLoading}
                                sx={{ ...pillButtonSx, color: "var(--text-secondary)" }}
                            >
                                Cancel
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            disableElevation
                            startIcon={<SaveOutlined />}
                            onClick={handleSubmit}
                            disabled={disableAllFields || !configExamId}
                            sx={pillButtonSx}
                        >
                            {savedConfig ? "Save changes" : "Save configuration"}
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="mx-auto w-full max-w-[1400px] px-4 pb-24 pt-6 sm:px-6 lg:px-8">

            {/* Page header */}
            <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">
                        Examinations
                    </p>
                    <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[28px]">Results</h1>
                    <p className="mt-1 max-w-xl text-sm text-slate-500">
                        Publish and review exam results, set up marks and grades, and share result links.
                    </p>
                </div>
                <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<RefreshOutlined />}
                    onClick={refresh}
                    disabled={loadingClasses}
                    sx={{ ...pillButtonSx, borderColor: "#e2e8f0", color: "#334155" }}
                >
                    Refresh
                </Button>
            </header>

            {/* Marksheet template */}
            <section className="mb-6 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                        <ImageOutlined fontSize="small" />
                    </span>
                    <div className="min-w-0">
                        <h2 className="text-sm font-semibold text-slate-800">Marksheet template</h2>
                        <p className="text-sm text-slate-500">
                            {savedTemplate?.name ? (
                                <>In use: <span className="font-medium text-slate-700">{savedTemplate.name}</span></>
                            ) : (
                                "Choose the design used when students download their marksheet."
                            )}
                        </p>
                    </div>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <TextField
                        select
                        size="small"
                        label="Template design"
                        value={selectedTemplate?.name && templates.some((item) => item.name === selectedTemplate.name) ? selectedTemplate.name : ""}
                        onChange={(e) => {
                            const match = templates.find((item) => item.name === e.target.value)
                            if (match) setSelectedTemplate({ id: match.id, name: match.name })
                        }}
                        sx={{ ...fieldSx, minWidth: { sm: 240 } }}
                    >
                        {templates.map((item) => (
                            <MenuItem key={item.id ?? item.name} value={item.name}>{item.name}</MenuItem>
                        ))}
                    </TextField>
                    <div className="flex gap-2">
                        <Button
                            variant="outlined"
                            onClick={() => setOpenTemplateView(true)}
                            disabled={!templateImage}
                            sx={{ ...pillButtonSx, flex: 1 }}
                        >
                            Preview
                        </Button>
                        <Tooltip title={canCreate ? "" : "You don't have permission to change the template"} arrow disableHoverListener={canCreate}>
                            <span className="flex flex-1">
                                <Button
                                    variant="contained"
                                    disableElevation
                                    onClick={applyTemplate}
                                    disabled={!canCreate || !templateChanged}
                                    sx={{ ...pillButtonSx, flex: 1 }}
                                >
                                    Use template
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                </div>
            </section>

            {!loadingClasses && classes.length === 0 ? (
                <EmptyState
                    icon={<SchoolOutlined />}
                    title={classesState.isError ? "Couldn't load classes" : "No classes found"}
                    description={
                        classesState.isError
                            ? "Please check your connection and try again."
                            : "Create a class and its examinations to manage results."
                    }
                    actionLabel={classesState.isError ? "Retry" : undefined}
                    onAction={classesState.isError ? fetchPreData : undefined}
                />
            ) : (
                <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">

                    {/* Classes */}
                    <aside className="lg:sticky lg:top-4 lg:self-start">
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-sm font-semibold text-slate-700">Classes</h2>
                            <span className="text-xs tabular-nums text-slate-400">{classes.length}</span>
                        </div>
                        <ClassList
                            classes={classes}
                            activeId={activeClassId}
                            onSelect={selectClass}
                            loading={loadingClasses}
                            getMeta={examCountFor}
                        />
                    </aside>

                    {/* Selected class */}
                    <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="h-1">
                            {(examsState.isFetching || markConfigState.isFetching || classStudentsState.isFetching) && (
                                <LinearProgress sx={{ height: 3 }} />
                            )}
                        </div>

                        <div className="px-4 pt-4 sm:px-5">
                            <h2 className="truncate text-lg font-semibold text-slate-900">
                                {activeClass?.class_name || "Select a class"}
                            </h2>
                            <p className="text-sm text-slate-500">
                                {classExamList.length} examination{classExamList.length === 1 ? "" : "s"}
                                {" · "}
                                {classStudentsState.isFetching && classStudents.length === 0
                                    ? "loading students…"
                                    : `${classStudents.length} student${classStudents.length === 1 ? "" : "s"}`}
                            </p>
                        </div>

                        <div role="tablist" aria-label="Class sections" className="mt-4 flex gap-1 overflow-x-auto border-b border-slate-200 px-4 sm:px-5">
                            {[
                                { key: "exams", label: "Exams & results", icon: <EventNoteOutlined sx={{ fontSize: 16 }} /> },
                                { key: "config", label: "Marks & grading", icon: <TuneOutlined sx={{ fontSize: 16 }} /> },
                                { key: "students", label: `Students (${classStudents.length})`, icon: <GroupsOutlined sx={{ fontSize: 16 }} /> }
                            ].map((item) => {
                                const active = tab === item.key
                                return (
                                    <button
                                        key={item.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        onClick={() => setTab(item.key)}
                                        className={[
                                            "-mb-px flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-sm transition-colors",
                                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500",
                                            active
                                                ? "border-indigo-600 font-semibold text-indigo-700"
                                                : "border-transparent text-slate-500 hover:text-slate-800"
                                        ].join(" ")}
                                    >
                                        {item.icon}
                                        {item.label}
                                    </button>
                                )
                            })}
                        </div>

                        {tab === "exams" && renderExams()}
                        {tab === "config" && renderConfig()}
                        {tab === "students" && renderStudents()}
                    </section>
                </div>
            )}

            <ImportDataModal
                open={openImport}
                downloadTemplate={handleDownloadTemplate}
                onClose={() => setOpenImport(false)}
                onImportResult={handleImportResult}
                selectedExam={selectedRow}
                setIsSubWiseTemplate={setIsSubWiseTemplate}
                setIsMinorSubjectTemplate={setIsMinorSubjectTemplate}
                setSubjectName={setSubjectName}
                subjectName={subjectName}
                downloadSubWiseTemplate={handleDownloadSubjectTemplate}
                downloadMinorSubjectTemplate={handleDownloadMinorTemplate}
            />
            <ImportResultPopover
                open={openTempCTable}
                data={tempCTableData}
                onClose={() => setOpenTempCTable(false)}
                selectedRow={selectedRow}
                selectedClass={activeClass}
                onSubmit={handlePublish}
                classExamList={classExamList}
                isSubjectWiseTemplate={isSubjectWiseTemplate}
                isMinorSubjectTemplate={isMinorSubjectTemplate}
            />
            <ResultViewDialog
                open={openPreviewResult}
                data={resultData}
                minorResults={minorResultData}
                onClose={() => {
                    setOpenPreviewResult(false)
                    // Clear only the dialog's editing state, not the marks setup
                    dispatch(updateFields(RESULT_VIEW_KEYS))
                }}
                updateStudentMark={updateStudentMark}
            />
            <ApplyConfigDialog
                open={openApplyConfig}
                onClose={() => setOpenApplyConfig(false)}
                config={copiedConfig}
                classList={classes}
                examList={examList}
                onApply={handleApplyConfig}
                applying={applyingConfig}
            />
            <FullScreenImageViewer
                imageUrl={templateImage}
                onClose={() => setOpenTemplateView(false)}
                open={openTemplateView}
                title="Marksheet Template Design"
            />

            {blocking && <Loader show={true} />}
        </div>
    )
}
